import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, PhoneNumberInput } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { MagicIcon, TriangleDownIcon } from '@modernloop/shared/icons';
import { Alert, AlertTitle, Button, Stack, TextField, Typography } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  EmployeeByIdsQuery,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  SelfScheduleInterviewMeetingLocation_InterviewMeetingLocationFragment,
  VideoMeetingLinkScope,
  useSelfScheduleMeetingLocationZoomUserBySchedulerIdQuery,
} from 'src/generated/mloop-graphql';

import VideoMeetingHost from 'src/entities/InterviewMeetingLocationModal/InterviewMeetingLocationHost/VideoMeetingHost';
import InterviewMeetingLocationMenu from 'src/entities/InterviewMeetingLocationModal/InterviewMeetingLocationMenu';
import VideoMeetingLinkScopeRadioGroup from 'src/entities/InterviewMeetingLocationModal/VideoMeetingLinkScopeRadioGroup';
import {
  isSelfScheduleVideoMeetingLinkScopeLocationType,
  resolveSelfScheduleVideoMeetingLinkScope,
} from 'src/entities/InterviewMeetingLocationModal/utils';
import { ZoomUserDataWithCategory } from 'src/entities/Select/ZoomUserSelect/types';
import ZoomUserSelectWithSmartOptions, { SmartOptions } from 'src/entities/Select/ZoomUserSelectWithSmartOptions';

import useEmployeeId from 'src/hooks/useEmployeeId';

type Fragments = {
  interviewMeetingLocation: SelfScheduleInterviewMeetingLocation_InterviewMeetingLocationFragment | undefined;
};

type Props = {
  missingEmployeesData: EmployeeByIdsQuery | undefined;
  onChange: (value: InterviewMeetingLocationSettingsInput) => void;
};

const SelfScheduleInterviewMeetingLocation: FCWithFragments<Fragments, Props> = ({
  interviewMeetingLocation,
  missingEmployeesData,
  onChange,
}) => {
  const { t } = useTranslation();
  const orgVideoMeetingLinkScopeEnabled = useFlag('org_video_meeting_link_scope');
  const schedulerId = useEmployeeId();
  const { data, loading } = useSelfScheduleMeetingLocationZoomUserBySchedulerIdQuery({
    variables: { schedulerId },
  });

  // Resolve the video-meeting-link scope on every settings payload so it round-trips through save.
  // The shared resolver enforces the backend invariants: non-link/flag-off payloads carry no scope,
  // and interviewer-hosted locations always resolve to UNIQUE (never ONE_LINK).
  const handleChange = (value: InterviewMeetingLocationSettingsInput) =>
    onChange({
      ...value,
      videoMeetingLinkScope: resolveSelfScheduleVideoMeetingLinkScope({
        enabled: orgVideoMeetingLinkScopeEnabled,
        interviewMeetingLocationType: value.interviewMeetingLocationType,
        dynamicHost: value.dynamicHost,
        existingScope: value.videoMeetingLinkScope ?? interviewMeetingLocation?.videoMeetingLinkScope,
      }),
    });

  const isInterviewersHost = interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Interviewers;
  const showVideoMeetingLinkScope =
    orgVideoMeetingLinkScopeEnabled &&
    isSelfScheduleVideoMeetingLinkScopeLocationType(interviewMeetingLocation?.interviewMeetingLocationType);
  const resolvedVideoMeetingLinkScope = isInterviewersHost
    ? VideoMeetingLinkScope.UniqueLinkPerInterview
    : interviewMeetingLocation?.videoMeetingLinkScope;
  const videoMeetingLinkScopeValue =
    resolvedVideoMeetingLinkScope && resolvedVideoMeetingLinkScope !== VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet
      ? resolvedVideoMeetingLinkScope
      : VideoMeetingLinkScope.OneLink;

  const handleVideoMeetingLinkScopeChange = (videoMeetingLinkScope: VideoMeetingLinkScope) => {
    handleChange({
      interviewMeetingLocationType:
        interviewMeetingLocation?.interviewMeetingLocationType || InterviewMeetingLocationType.None,
      dynamicHost: interviewMeetingLocation?.dynamicHost || undefined,
      hostEmployeeId: interviewMeetingLocation?.hostEmployeeId || undefined,
      remoteVideoMeetingHostUserId: interviewMeetingLocation?.remoteVideoMeetingHostUserId || undefined,
      customLocation: interviewMeetingLocation?.customLocation || undefined,
      videoMeetingLinkScope,
    });
  };

  const handleInterviewMeetingLocationChange = (interviewMeetingLocationType: InterviewMeetingLocationType) => {
    let dynamicHost: DynamicInterviewMeetingHost | undefined;
    let customLocation: string | undefined;
    let remoteVideoMeetingHostUserId: string | undefined;

    if (
      [
        InterviewMeetingLocationType.Zoom,
        InterviewMeetingLocationType.GoogleMeet,
        InterviewMeetingLocationType.MicrosoftTeams,
      ].includes(interviewMeetingLocationType)
    ) {
      dynamicHost = DynamicInterviewMeetingHost.Scheduler;

      // For Zoom, automatically set the scheduler's Zoom user ID to the form state
      if (interviewMeetingLocationType === InterviewMeetingLocationType.Zoom && data?.zoomUsersByIds?.length) {
        remoteVideoMeetingHostUserId = data.zoomUsersByIds[0].zoomUserId;
      }
    }

    if (
      [
        InterviewMeetingLocationType.CustomLink,
        InterviewMeetingLocationType.CustomPhone,
        InterviewMeetingLocationType.CandidatePhone,
        InterviewMeetingLocationType.CustomText,
      ].includes(interviewMeetingLocationType)
    ) {
      customLocation = '';
    }

    handleChange({
      interviewMeetingLocationType,
      dynamicHost,
      customLocation,
      hostEmployeeId: undefined,
      remoteVideoMeetingHostUserId:
        remoteVideoMeetingHostUserId || interviewMeetingLocation?.remoteVideoMeetingHostUserId,
    });
  };

  const handleZoomUserChanged = (value: ZoomUserDataWithCategory) => {
    if (value.id === SmartOptions.Interviewer || value.id === SmartOptions.Scheduler) {
      handleChange({
        interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
        dynamicHost:
          value.id === SmartOptions.Interviewer
            ? DynamicInterviewMeetingHost.Interviewers
            : DynamicInterviewMeetingHost.Scheduler,
      });
    } else {
      handleChange({
        interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
        dynamicHost: DynamicInterviewMeetingHost.CustomZoomHost,
        hostEmployeeId: value?.employeeId,
        remoteVideoMeetingHostUserId: value.zoomUserId,
      });
    }
  };

  let zoomUserId = interviewMeetingLocation?.remoteVideoMeetingHostUserId;
  if (interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Scheduler && data?.zoomUsersByIds?.length) {
    zoomUserId = data.zoomUsersByIds[0].zoomUserId;
  } else if (interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
    zoomUserId = SmartOptions.Interviewer;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">
        {t('hiring-dashboard-schedule:schedule.selfScheduleOptions.scheduleLocation')}
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ width: '50%', flexShrink: 0 }}>
          {t('hiring-dashboard-common:common.location.title')}
        </Typography>
        <InterviewMeetingLocationMenu
          disabled={loading}
          interviewMeetingLocationType={
            interviewMeetingLocation?.interviewMeetingLocationType || InterviewMeetingLocationType.None
          }
          onChange={handleInterviewMeetingLocationChange}
        />
      </Stack>

      {interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ width: '50%' }}>{t('hiring-dashboard-common:common.location.hostLabel')}</Typography>
          <ZoomUserSelectWithSmartOptions
            hideSchedulerSmartOption
            value={zoomUserId || undefined}
            panelInterviewerIds={[]}
            onChange={handleZoomUserChanged}
            getLabel={(value) => {
              return (
                <Button
                  fullWidth
                  sx={{ borderRadius: '6px' }}
                  startIcon={
                    interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Interviewers ||
                    interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Scheduler ? (
                      <MagicIcon color="primary" />
                    ) : undefined
                  }
                >
                  <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
                    <Typography noWrap>{value}</Typography>
                    <TriangleDownIcon />
                  </Stack>
                </Button>
              );
            }}
          />
        </Stack>
      )}

      {(interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.GoogleMeet ||
        interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.MicrosoftTeams) &&
        interviewMeetingLocation.interviewMeetingLocationType && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ width: '50%', flexShrink: 0 }}>
              {t('hiring-dashboard-common:common.location.hostLabel')}
            </Typography>
            <VideoMeetingHost
              disabled={false}
              interviewMeetingLocationSettingPref={interviewMeetingLocation}
              onChange={(value) => handleChange(value)}
            />
          </Stack>
        )}

      {(interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomLink ||
        interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomText) && (
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ display: 'flex', alignItems: 'center', width: '50%', flexShrink: 0 }}>
            {interviewMeetingLocation.interviewMeetingLocationType === InterviewMeetingLocationType.CustomLink
              ? t('hiring-dashboard-common:common.location.customLink')
              : undefined}
            {interviewMeetingLocation.interviewMeetingLocationType === InterviewMeetingLocationType.CustomText
              ? t('hiring-dashboard-common:common.location.customText')
              : undefined}
          </Typography>
          <TextField
            fullWidth
            key={interviewMeetingLocation.interviewMeetingLocationType}
            inputProps={{ startAdornment: <></> }}
            defaultValue={interviewMeetingLocation.customLocation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleChange({
                interviewMeetingLocationType: interviewMeetingLocation.interviewMeetingLocationType,
                customLocation: e.target.value,
              });
            }}
          />
        </Stack>
      )}

      {interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomPhone && (
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ display: 'flex', alignItems: 'center', width: '50%', flexShrink: 0 }}>
            {interviewMeetingLocation.interviewMeetingLocationType === InterviewMeetingLocationType.CustomPhone
              ? t('hiring-dashboard-common:common.location.phoneNumberLabel')
              : undefined}
          </Typography>
          <PhoneNumberInput
            value={interviewMeetingLocation.customLocation || ''}
            setPhoneNumber={(phoneNumber: string) => {
              handleChange({
                interviewMeetingLocationType: interviewMeetingLocation.interviewMeetingLocationType,
                customLocation: phoneNumber,
              });
            }}
          />
        </Stack>
      )}

      {interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Interviewers && (
        <Alert severity="info">{t('hiring-dashboard-common:common.location.hostInterviewersAlert')}</Alert>
      )}

      {showVideoMeetingLinkScope && (
        <VideoMeetingLinkScopeRadioGroup
          value={videoMeetingLinkScopeValue}
          isInterviewersHost={isInterviewersHost}
          onChange={handleVideoMeetingLinkScopeChange}
        />
      )}

      {missingEmployeesData && missingEmployeesData.employeeByIds?.length && (
        <Alert severity="warning">
          <AlertTitle>{t('hiring-dashboard-schedule:schedule.selfScheduleOptions.missingZoomHosts.title')}</AlertTitle>
          {t('hiring-dashboard-schedule:schedule.selfScheduleOptions.missingZoomHosts.description', {
            count: missingEmployeesData.employeeByIds.length,
            names: missingEmployeesData.employeeByIds.map((employee) => employee.fullName).join(', '),
          })}
        </Alert>
      )}
    </Stack>
  );
};

SelfScheduleInterviewMeetingLocation.fragments = {
  interviewMeetingLocation: gql`
    ${VideoMeetingHost.fragments.interviewMeetingLocationSettingPref}
    fragment SelfScheduleInterviewMeetingLocation_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      interviewMeetingLocationType
      videoMeetingLinkScope
      dynamicHost
      hostEmployeeId
      remoteVideoMeetingHostUserId
      customLocation
      ...VideoMeetingHost_interviewMeetingLocationSettingPref
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SelfScheduleMeetingLocationZoomUserBySchedulerIdQuery = gql`
  query SelfScheduleMeetingLocationZoomUserBySchedulerId($schedulerId: uuid!) {
    zoomUsersByIds(input: { employeeIds: [$schedulerId] }) {
      zoomUserId
      employeeId
    }
  }
`;

export default SelfScheduleInterviewMeetingLocation;
